import React, { useState, useEffect } from "react";
import { 
  Grid, 
  Button, 
  Divider,
  FormControl,
  TextField,
  MenuItem,
  CircularProgress,
  InputAdornment
} from "@material-ui/core";
import Title from "../../../Title";
import _ from "lodash";
import { useStyles } from "./styles";
import clsx from "clsx";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useHasPermissions from "../../../../hooks/useHasPermissions";
import useRoamingCCTerminalState from "../../../../state/slices/cashier/useRoamingCCTerminalState";
import { useGatewayContext } from "../../../../providers/CreditCardGatewayProvider";
import { useEnqueueSnackbar } from "../../../../hooks/useEnqueueSnackbar";
import { PAYMENT_GATEWAY } from "../../../../constants";
import { useFlags } from "launchdarkly-react-client-sdk";
import useCurrentFacility from "../../../../hooks/useCurrentFacility";

const RoamingCCTerminalForm = ({ handleSave, handleEdit, handleDelete, handleCancel, roamingCCTerminal, entityID }) => {
  const classes = useStyles();
  const [creditCardGateways, setCreditCardGateways] = useState([]);
  const [isLoadingGateways, setIsLoadingGateways] = useState(false);
  const [selectedGatewayId, setSelectedGatewayId] = useState(roamingCCTerminal?.creditCardGatewayId || "");
  const enqueueSnackbar = useEnqueueSnackbar();
  const { hasPermissions } = useHasPermissions();
  const DeletePermission = hasPermissions(["roamingcreditcardterminalsetup.delete"]);
  const { planetA920Pro: planetA920proLDFlag } = useFlags();
  const { facilityID } = useCurrentFacility();

  const isElavon = selectedGatewayId === PAYMENT_GATEWAY.ELAVON;
  const isEditMode = roamingCCTerminal.roamingCCTerminalID !== undefined;

  const { roamingCCTerminals } = useRoamingCCTerminalState();
  const {
    functions: { getCreditCardGateways, getCreditCardGatewaySettingsByEntityID },
  } = useGatewayContext();

  const roamingCCTerminalSchema = Yup.object().shape({
    displayName: Yup.string()
    .required("Required")
    .when('creditCardGatewayId', (creditCardGatewayId, schema) => {
      return creditCardGatewayId === PAYMENT_GATEWAY.ELAVON
        ? schema.max(12, "Maximum of 12 characters for Elavon device names")
        : schema.max(25, "Maximum of 25 characters");
    })
    .test('unique','Device Name already in use.',(value) =>{
      const displayNames = roamingCCTerminals
      .filter(roamingCCT => roamingCCT.roamingCCTerminalID !== roamingCCTerminal.roamingCCTerminalID)
      .map((roamingCCT) => roamingCCT.displayName);
      return !displayNames.includes(value);
    }),
    terminalSerialNo: Yup
    .string()
    .required('Required')
    .test('unique','Terminal ID already in use.',(value) =>{
      const terminalSerialNos = roamingCCTerminals
      .filter(roamingCCT => roamingCCT.roamingCCTerminalID !== roamingCCTerminal.roamingCCTerminalID)
      .map((roamingCCT) => roamingCCT.terminalSerialNo);
      return (terminalSerialNos.length) !== new Set([...terminalSerialNos, value]).size;
    }),
    pairingCode: Yup
    .string()
    .when('creditCardGatewayId', (creditCardGatewayId, schema) => {
      return creditCardGatewayId === PAYMENT_GATEWAY.ELAVON
        ? schema.required('Pairing Code is required for Elavon devices').max(6, "Pairing code must be 6 characters")
        : schema.notRequired();
    }),
    creditCardGatewayId: Yup.number().required(),
  });

  const fetchCreditCardGateways = async () => {
    setIsLoadingGateways(true);
    try {
      const gatewaysResponse = await getCreditCardGateways();
      if (gatewaysResponse?.data?.length > 0) {
        let filtered = gatewaysResponse.data;
        try {
          const settingsResponse = await getCreditCardGatewaySettingsByEntityID(facilityID);
          const activeSettings = (settingsResponse?.data || [])
            .filter(s => s.active);
          if (activeSettings.length > 0) {
            const activeGatewayIds = activeSettings.map(s => s.setting);
            const matched = gatewaysResponse.data.filter(
              gw => activeGatewayIds.includes(gw.creditCardGatewayID)
            );
            if (matched.length > 0) {
              filtered = matched;
            }
          }
        } catch (settingsErr) {
          // Settings fetch failed (e.g. 404) — show all gateways as fallback
        }
        setCreditCardGateways(filtered);
        // When editing, ensure the terminal's current gateway is in the dropdown
        if (roamingCCTerminal.roamingCCTerminalID && roamingCCTerminal.creditCardGatewayId) {
          const currentGwInList = filtered.some(gw => {
            const numericId = gw.creditCardGatewayID === "Planet" ? PAYMENT_GATEWAY.PLANET : gw.creditCardGatewayID === "Windcave" ? PAYMENT_GATEWAY.WINDCAVE : PAYMENT_GATEWAY.ELAVON;
            return numericId === roamingCCTerminal.creditCardGatewayId;
          });
          // Is the terminal's current gateway missing from the filtered list?
          if (!currentGwInList) {
            const currentGw = gatewaysResponse.data.find(gw => {
              const numericId = gw.creditCardGatewayID === "Planet" ? PAYMENT_GATEWAY.PLANET : gw.creditCardGatewayID === "Windcave" ? PAYMENT_GATEWAY.WINDCAVE : PAYMENT_GATEWAY.ELAVON;
              return numericId === roamingCCTerminal.creditCardGatewayId;
            });
            if (currentGw) {
              setCreditCardGateways([...filtered, currentGw]);
            }
          }
        }
      } else {
        enqueueSnackbar("Could not find any gateways to display!", {
          variant: "error",
					tag: "FailedToFindGatewaysToDisplay"
				});
      }
    } catch(err) {
      enqueueSnackbar("An error occurred while fetching gateways!", {
				variant: "error",
				tag: "FailedToFetchGateways"
			});
    } finally {
      setIsLoadingGateways(false);
    }
  }

  useEffect(() => {
    planetA920proLDFlag && fetchCreditCardGateways();
  },[planetA920proLDFlag]);
  
  const {
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm({
    defaultValues: { ...roamingCCTerminal, pairingCode: "" },
    resolver: yupResolver(roamingCCTerminalSchema),
    shouldUnregister: false,
    mode: "onChange"
  });

  const watchedGatewayId = watch("creditCardGatewayId");
  const watchedDisplayName = watch("displayName");
  const gatewayHasChanged = React.useRef(false);

  useEffect(() => {
    setSelectedGatewayId(watchedGatewayId);
    if (gatewayHasChanged.current && watchedDisplayName) {
      trigger("displayName");
    }
    gatewayHasChanged.current = true;
  }, [watchedGatewayId]);

  const onSubmit = async data => {
    if (roamingCCTerminal.roamingCCTerminalID === undefined) {
      await handleSave(data);
    } else {
      await handleEdit(data);
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={8} md={8} className={clsx([classes.roamingCCTTitle])}>
          <Title>Credit Card Terminal</Title>
        </Grid>
        {(roamingCCTerminal.roamingCCTerminalID && DeletePermission === true) && (
        <Grid item xs={4} md={4} className={clsx([classes.drawerContainer])} >
          <Button
            className={clsx("deleteBtn", classes.btnControl)}
            name="delete"
            variant="contained"
            color="secondary"
            onClick={handleDelete}
            disabled={isSubmitting}
            data-testid="deleteBtn"
          >
          Delete
          </Button>
        </Grid>
        )}
      </Grid>
      <Divider></Divider>
      <Grid container className={clsx([classes.drawerContainer])}>
        {planetA920proLDFlag && 
          <Grid item xs={12} className={clsx([classes.drawerItems])}>
            <Controller
              name="creditCardGatewayId"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  {...field}
                  id="creditCardGatewayId"
                  value={field?.value}
                  label="Device Type"
                  select
                  variant="outlined"
                  disabled={isLoadingGateways}
                  helperText={isLoadingGateways ? "Loading available device types..." : ""}
                  InputProps={{
                    endAdornment: isLoadingGateways ? (
                      <InputAdornment position="end" style={{ marginRight: 16 }}>
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ) : null
                  }}
                >
                  {creditCardGateways?.length > 0 &&
                    creditCardGateways.map((gateway) => (
                      <MenuItem
                        key={gateway.creditCardGatewayID}
                        value={gateway.creditCardGatewayID === "Planet" ? PAYMENT_GATEWAY.PLANET : gateway.creditCardGatewayID === "Windcave" ? PAYMENT_GATEWAY.WINDCAVE : PAYMENT_GATEWAY.ELAVON}
                        id={gateway.creditCardGatewayID}
                      >
                        {gateway.creditCardGatewayID === "Planet" ? "Planet A920Pro" : gateway.creditCardGatewayID === "Windcave" ? "Windcave Move 5000" : "Elavon Move 5000"}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            />
          </Grid>
        }
        <Grid item xs={12} sm={12} className={clsx([classes.drawerItems])}>
          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <TextField
                  {...field}
                  onBlur={(e) => {
                    field.onChange(e.target.value.trim());
                  }}
                  id="displayName"
                  className={clsx("deviceName", classes.flexInput)}
                  label="Device Name"
                  variant="outlined"
                  error={!!errors.displayName}
                  helperText={errors.displayName && errors.displayName.message}
                  inputProps={{ maxLength: 25 }}
                />
              </FormControl>
            )}
          />                        
        </Grid>
        <Grid item xs={12} sm={12} className={clsx([classes.drawerItems])}>
          <Controller
            name="terminalSerialNo"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <TextField
                  {...field}
                  onBlur={(e) => {
                    field.onChange(e.target.value.trim());
                  }}
                  id="terminalSerialNo"
                  label="Terminal ID"
                  className={clsx("uniqueID", classes.flexInput)}
                  variant="outlined"
                  disabled={isElavon && isEditMode}
                  error={!!errors.terminalSerialNo}
                  helperText={
                    errors.terminalSerialNo
                      ? errors.terminalSerialNo.message
                      : ""
                  }
                />
              </FormControl>
            )}
          />
        </Grid>
        {isElavon && (
          <Grid item xs={12} sm={12} className={clsx([classes.drawerItems])}>
            <Controller
              name="pairingCode"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <TextField
                    {...field}
                    onBlur={(e) => {
                      field.onChange(e.target.value.trim());
                    }}
                    id="pairingCode"
                    label="Pairing Code"
                    className={clsx("pairingCode", classes.flexInput)}
                    variant="outlined"
                    disabled={isEditMode}
                    error={!!errors.pairingCode}
                    helperText={
                      errors.pairingCode
                        ? errors.pairingCode.message
                        : isEditMode ? "" : "Enter the 6-character code shown on the device"
                    }
                    inputProps={{ maxLength: 6 }}
                  />
                </FormControl>
              )}
            />
          </Grid>
        )}
      </Grid>
      <Divider></Divider>
      <Grid container className={clsx([classes.footerContainer])}>
        <Grid item xs={12} sm={12} className={clsx([classes.drawerItems])}>
          <Button
            className={clsx("cancelBtn", classes.btnControl)}
            name="cancel"
            variant="contained"
            onClick={handleCancel}
            disabled={isSubmitting}
            data-testid="cancelBtn"
          >
          Close
          </Button>
          <Button
            color="primary"
            name="submit"
            type="submit"
            variant="contained"
            className={clsx("saveBtn", classes.btnControl)}
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || !isValid || !isDirty}
            data-testid="submitBtn"
          >
          Save
          </Button>
        </Grid>
      </Grid>
    </>
  );
};
export default RoamingCCTerminalForm;